import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { consumePostAuthRedirect } from "@/lib/postAuthRedirect";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const PENDING_MARKETING_KEY = "huelo:pending_marketing_opt_in";
const PENDING_FIRST_NAME_KEY = "huelo:pending_first_name";
const PENDING_LAST_NAME_KEY = "huelo:pending_last_name";

const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Never hang forever on a stuck auth lock: cap the wait at 8s.
      const timeout = new Promise<null>((r) => setTimeout(() => r(null), 8000));
      const session = await Promise.race([
        supabase.auth.getSession().then(({ data }) => data.session),
        timeout,
      ]);
      if (cancelled) return;
      if (!session) {
        toast({ title: "Sign-in failed", description: "Please try again.", variant: "destructive" });
        navigate("/", { replace: true });
        return;
      }

      // Persist any pending marketing opt-in / names captured on the auth
      // page before the OAuth redirect. For Google sign-ins, also read names
      // straight from user_metadata (given_name / family_name / full_name).
      try {
        const updates: {
          marketing_opt_in?: boolean;
          marketing_opt_in_at?: string;
          marketing_prompt_seen?: boolean;
          first_name?: string;
          last_name?: string;
        } = {};

        const pendingMarketing = localStorage.getItem(PENDING_MARKETING_KEY);
        if (pendingMarketing !== null) {
          localStorage.removeItem(PENDING_MARKETING_KEY);
          updates.marketing_prompt_seen = true;
          if (pendingMarketing === "1") {
            updates.marketing_opt_in = true;
            updates.marketing_opt_in_at = new Date().toISOString();
          }
        }


        const pendingFirst = localStorage.getItem(PENDING_FIRST_NAME_KEY);
        const pendingLast = localStorage.getItem(PENDING_LAST_NAME_KEY);
        if (pendingFirst !== null) localStorage.removeItem(PENDING_FIRST_NAME_KEY);
        if (pendingLast !== null) localStorage.removeItem(PENDING_LAST_NAME_KEY);

        const meta = (session.user.user_metadata ?? {}) as {
          first_name?: string;
          last_name?: string;
          given_name?: string;
          family_name?: string;
          full_name?: string;
          name?: string;
        };
        const fullFromMeta = (meta.full_name ?? meta.name ?? "").trim();
        const parts = fullFromMeta ? fullFromMeta.split(/\s+/) : [];
        const firstFromMeta =
          meta.first_name?.trim() ||
          meta.given_name?.trim() ||
          parts[0] ||
          "";
        const lastFromMeta =
          meta.last_name?.trim() ||
          meta.family_name?.trim() ||
          (parts.length > 1 ? parts.slice(1).join(" ") : "");

        const firstName = (pendingFirst || firstFromMeta || "").trim();
        const lastName = (pendingLast || lastFromMeta || "").trim();

        // Read existing profile so we don't overwrite fields the user
        // already edited in account settings.
        const { data: existing } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (firstName && !existing?.first_name) updates.first_name = firstName;
        if (lastName && !existing?.last_name) updates.last_name = lastName;

        if (Object.keys(updates).length > 0) {
          await supabase
            .from("profiles")
            .update(updates)
            .eq("user_id", session.user.id);
        }
      } catch {
        // ignore — non-critical
      }

      navigate(consumePostAuthRedirect(), { replace: true });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center gap-3">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Finishing sign-in…</p>
    </div>
  );
};

export default AuthCallback;
